import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ScaleIcon, HomeIcon, BriefcaseIcon, UsersIcon, FileTextIcon, LandmarkIcon, ChevronRightIcon, CheckCircle2Icon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.8,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {
    opacity: 0
  },
  whileInView: {
    opacity: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    staggerChildren: 0.2
  }
};
export function Home() {
  return <div className="w-full" data-id="element-279">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden" data-id="element-280">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80&w=2000")'
      }} data-id="element-281">
          <div className="absolute inset-0 bg-brand-green/30 mix-blend-multiply" data-id="element-282"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-brand-charcoal/60 via-brand-charcoal/40 to-brand-charcoal/80" data-id="element-283"></div>
        </div>

        <div className="relative z-10 max-w-container mx-auto px-6 text-center text-white mt-20" data-id="element-284">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} data-id="element-285">
            <h1 className="font-serif text-5xl md:text-7xl font-bold mb-6 leading-tight drop-shadow-lg" data-id="element-286">
              Legal Solutions Built on <br className="hidden md:block" data-id="element-287" />
              <span className="text-brand-gold italic" data-id="element-288">
                Trust, Integrity & Results.
              </span>
            </h1>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 0.6
        }} className="max-w-3xl mx-auto mb-10" data-id="element-289">
            <p className="text-lg md:text-xl font-light leading-relaxed text-gray-200 drop-shadow-md" data-id="element-290">
              McErick Attorneys provides professional legal services to
              individuals, families, entrepreneurs and businesses throughout
              Botswana. We are committed to delivering practical legal solutions
              with professionalism, integrity and efficiency.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.9
        }} className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-291">
            <Link to="/contact" className="px-8 py-4 bg-brand-gold hover:bg-brand-gold-light text-white rounded-full font-medium transition-all shadow-lg hover:shadow-xl w-full sm:w-auto" data-id="element-292">
              Book Consultation
            </Link>
            <Link to="/practice-areas" className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-full font-medium transition-all w-full sm:w-auto" data-id="element-293">
              Explore Our Services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-brand-offwhite" data-id="element-294">
        <div className="max-w-container mx-auto px-6" data-id="element-295">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-296">
            <motion.div {...fadeInUp} data-id="element-297">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-green mb-6" data-id="element-298">
                Your Trusted Legal Partner
              </h2>
              <div className="w-20 h-1 bg-brand-gold mb-8" data-id="element-299"></div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-300">
                At McErick Attorneys, we believe that effective legal
                representation begins with understanding our clients.
              </p>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed" data-id="element-301">
                Whether you require assistance with property transfers, divorce
                proceedings, debt recovery, labour disputes or commercial legal
                matters, our team is committed to protecting your interests
                through practical legal solutions and professional service.
              </p>
              <Link to="/about" className="inline-flex items-center text-brand-green font-semibold hover:text-brand-gold transition-colors group" data-id="element-302">
                Learn More About Us
                <ChevronRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-303" />
              </Link>
            </motion.div>
            <motion.div {...fadeInUp} className="relative" data-id="element-304">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-card relative z-10" data-id="element-305">
                <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=1200" alt="Professional legal consultation" className="w-full h-full object-cover" data-id="element-306" />
              </div>
              <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-brand-green rounded-2xl -z-0 hidden md:block" data-id="element-307"></div>
              <div className="absolute -top-8 -right-8 w-32 h-32 border-4 border-brand-gold rounded-2xl -z-0 hidden md:block" data-id="element-308"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Practice Areas Preview */}
      <section className="py-24 bg-white" data-id="element-309">
        <div className="max-w-container mx-auto px-6" data-id="element-310">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-311">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-green mb-6" data-id="element-312">
              Areas of Practice
            </h2>
            <p className="text-lg text-gray-600" data-id="element-313">
              Comprehensive legal services tailored to meet the diverse needs of
              individuals and businesses in Botswana.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-314">
            {[{
            title: 'Conveyancing',
            desc: 'Property transfers, mortgage bonds, and deeds registration.',
            icon: HomeIcon,
            img: 'https://cdn.magicpatterns.com/uploads/95hdmJPwwsw14MzoD8zhsZ/Land_transfer.jpg'
          }, {
            title: 'Corporate Law',
            desc: 'Commercial contracts, compliance, and business advisory.',
            icon: BriefcaseIcon,
            img: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=800'
          }, {
            title: 'Family Law',
            desc: 'Divorce, custody, maintenance, and family disputes.',
            icon: UsersIcon,
            img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=800'
          }].map((service, idx) => <motion.div key={idx} variants={fadeInUp} className="group rounded-2xl overflow-hidden shadow-soft hover:shadow-card transition-all duration-300 bg-white border border-gray-100" data-id="element-315">
                <div className="relative h-48 overflow-hidden" data-id="element-316">
                  <div className="absolute inset-0 bg-brand-charcoal/20 group-hover:bg-brand-charcoal/10 transition-colors z-10" data-id="element-317"></div>
                  <img src={service.img} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-318" />
                  <div className="absolute bottom-4 left-4 z-20 bg-white p-3 rounded-xl shadow-md text-brand-green" data-id="element-319">
                    <service.icon className="w-6 h-6" data-id="element-320" />
                  </div>
                </div>
                <div className="p-8" data-id="element-321">
                  <h3 className="font-serif text-2xl font-bold text-brand-charcoal mb-3 group-hover:text-brand-green transition-colors" data-id="element-322">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-323">{service.desc}</p>
                  <Link to="/practice-areas" className="text-brand-gold font-medium flex items-center group-hover:text-brand-green transition-colors" data-id="element-324">
                    Learn More <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-325" />
                  </Link>
                </div>
              </motion.div>)}
          </motion.div>

          <div className="text-center mt-12" data-id="element-326">
            <Link to="/practice-areas" className="inline-block px-8 py-4 border-2 border-brand-green text-brand-green hover:bg-brand-green hover:text-white rounded-full font-medium transition-all" data-id="element-327">
              View All Practice Areas
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Conveyancing Section */}
      <section className="bg-brand-charcoal text-white overflow-hidden" data-id="element-328">
        <div className="flex flex-col lg:flex-row" data-id="element-329">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="lg:w-1/2 p-12 lg:p-24 flex flex-col justify-center" data-id="element-330">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-gold mb-6" data-id="element-331">
              Botswana's Property Transfer Specialists
            </h2>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed" data-id="element-332">
              Buying or selling property is one of the most significant
              financial decisions you'll ever make. McErick Attorneys provides
              professional conveyancing services from the initial agreement
              through registration of ownership, ensuring every transaction is
              handled efficiently and accurately.
            </p>
            <ul className="space-y-4 mb-10" data-id="element-333">
              {['Property Transfers', 'Mortgage Bonds', 'Sectional Titles', 'Deeds Registration'].map((item, i) => <li key={i} className="flex items-center text-gray-200" data-id="element-334">
                  <CheckCircle2Icon className="w-5 h-5 text-brand-gold mr-3" data-id="element-335" />
                  {item}
                </li>)}
            </ul>
            <div data-id="element-336">
              <Link to="/conveyancing" className="inline-block px-8 py-4 bg-brand-gold hover:bg-brand-gold-light text-white rounded-full font-medium transition-all shadow-lg" data-id="element-337">
                Start Your Property Transfer
              </Link>
            </div>
          </motion.div>
          <div className="lg:w-1/2 relative min-h-[400px] lg:min-h-full" data-id="element-338">
            <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80&w=1200" alt="Luxury property" className="absolute inset-0 w-full h-full object-cover" data-id="element-339" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 bg-brand-green text-center px-6 overflow-hidden" data-id="element-340">
        {/* Background image with brand overlay for readability */}
        <div className="absolute inset-0 z-0" data-id="element-341">
          <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=2000" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-342" />
          <div className="absolute inset-0 bg-brand-green/90" data-id="element-343"></div>
        </div>
        <motion.div {...fadeInUp} className="relative z-10 max-w-3xl mx-auto" data-id="element-344">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-345">
            Need Professional Legal Assistance?
          </h2>
          <p className="text-xl text-brand-offwhite/90 mb-10 leading-relaxed" data-id="element-346">
            Whether you are purchasing property, navigating family matters,
            recovering outstanding debts or seeking commercial legal advice, our
            experienced team is ready to assist.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-347">
            <Link to="/contact" className="px-8 py-4 bg-brand-gold hover:bg-brand-gold-light text-white rounded-full font-medium transition-all shadow-lg w-full sm:w-auto" data-id="element-348">
              Book Consultation
            </Link>
            <a href="tel:+2673165472" className="px-8 py-4 bg-transparent border border-white text-white hover:bg-white hover:text-brand-green rounded-full font-medium transition-all w-full sm:w-auto" data-id="element-349">
              Call Now
            </a>
          </div>
        </motion.div>
      </section>
    </div>;
}